import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import {
  Box,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faWarning } from '@fortawesome/pro-regular-svg-icons';
import {
  Banner,
  Button,
  ButtonText,
  FormFeedback,
  Input,
  InputProps
} from '@morressier/alembic';
import { useMutation } from '@tanstack/react-query';

import {
  AffiliationCountryList,
  fetchRinggoldOrganization
} from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import { IAuthorsValue } from 'src/components/Submission/GenericFieldComponents/Authors/AuthorsUpdateModal';
import { useFormContext } from 'react-hook-form';
import { CountrySearchSelect } from 'src/components/User/CountrySearchSelect';

export type RinggoldOrg = {
  name: string;
  ringgoldId: string;
  topLevelOrganizationName?: string | null;
  organizationType?: string | null;
  formattedLocation?: string | null;
  primaryDomain?: string | null;
};

interface Props {
  onOrganizationSelect: (organization: RinggoldOrg) => void;
  onAddUnverifiedAffiliation: () => void;
  setIsAddAffiliationManually: (status: boolean) => void;
  isAddAffiliationManually: boolean;
  selectedCountry: AffiliationCountryList | null;
  setSelectedCountry: (selectedCountry: AffiliationCountryList | null) => void;
  index: number;
  isVerified: boolean;
}

const AuthorRinggoldSearch = forwardRef<HTMLInputElement, InputProps & Props>(
  (
    {
      onOrganizationSelect,
      index,
      setIsAddAffiliationManually,
      isAddAffiliationManually,
      selectedCountry,
      setSelectedCountry,
      isVerified,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const suppressNextClose = useRef(false);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const { isOpen, onToggle, onClose } = useDisclosure();
    const {
      register,
      formState: { errors },
      clearErrors,
      setValue
    } = useFormContext<IAuthorsValue>();
    const {
      data: ringgoldOrgs,
      isLoading,
      mutateAsync: searchRinggoldOrgMutation
    } = useMutation({
      mutationFn: (searchTerm: string) =>
        fetchRinggoldOrganization(searchTerm, 0, 10)
    });

    const handleSearch = useCallback(
      debounce(async (value: string) => {
        // if value is less then 3 do not trigger the api
        // same validation is on backend side
        if (value.length < 3) {
          if (isOpen) {
            onToggle(); // Close the dropdown if it is open
          }
          return;
        }

        if (!isOpen) onToggle();
        await searchRinggoldOrgMutation(value);
      }, 500),
      [isOpen, onToggle, searchRinggoldOrgMutation]
    );
    const renderSearchFieldErrorMessage = (message?: string) => {
      let formattedMessage = message;

      switch (message) {
        case 'must match pattern "\\S"':
          formattedMessage = 'Affiliation cannot be blank';
          break;
        case 'must NOT have fewer than 3 characters':
          formattedMessage = 'Please type at least 3 characters to search';
          break;
        default:
          break;
      }

      return <FormFeedback variant="error">{formattedMessage}</FormFeedback>;
    };
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      handleSearch(value);
      return props?.onChange?.(e);
    };

    const handleAddUnverifiedAffiliations = () => {
      setSelectedCountry(null);
      setValue(`metadata.authors.${index}.affiliation`, '');
      setValue(`metadata.authors.${index}.city`, '');
      setValue(`metadata.authors.${index}.postalCode`, '');
      setIsAddAffiliationManually(true);
      clearErrors(`metadata.authors.${index}.affiliation`);
    };
    const renderAffiliationList = () => {
      // loading state
      if (isLoading) {
        return (
          <Flex
            flexDirection="column"
            gap={{ base: 4, md: 8 }}
            textAlign="center"
          >
            <Box bgColor="neutralSecondaryFill" padding={{ base: 4, md: 8 }}>
              <Spinner fontSize={40} aria-label="Loading affiliation results" />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">Loading...</Text>
              <Text textStyle="textBody" paddingX={2}>
                We are currently fetching Affiliation Results from the Ringgold
                database.
              </Text>
            </Flex>
          </Flex>
        );
      }

      // empty data state
      if (!isLoading && !ringgoldOrgs) {
        return (
          <Flex flexDirection="column" gap={8} textAlign="center">
            <Box bgColor="neutralSecondaryFill" padding={8}>
              <FontAwesomeIcon icon={faWarning} fontSize={40} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">No Results</Text>
              <Text textStyle="textBody" paddingX={2}>
                This organization isn’t in Ringgold’s database. You can add it
                manually, and provide details to help verify it.
              </Text>
              <Flex justifyContent="center" mt={3}>
                <Button
                  variant="primary"
                  onClick={() => {
                    handleAddUnverifiedAffiliations();
                  }}
                  data-cy="ringgold-add-unverified-affiliation-button"
                  aria-label="Add affiliation manually"
                >
                  Add Affiliation Manually
                </Button>
              </Flex>
            </Flex>
          </Flex>
        );
      }

      return (
        <List
          padding={'0 !important'}
          display="flex"
          flexDirection="column"
          gap={4}
        >
          <Text textStyle="textContext">
            {ringgoldOrgs.totalCount} Results Found
          </Text>
          {ringgoldOrgs?.organizations.map(org => {
            return (
              <ListItem
                as="button"
                type="button"
                role="option"
                aria-label={`Select ${org.name}`}
                cursor="pointer"
                key={org.ringgoldId}
                onClick={() => {
                  onOrganizationSelect(org);
                  onClosePopUp();
                }}
                data-cy={`ringgold-org-${org.ringgoldId}`}
                paddingY={{ base: 3, md: 1 }}
                paddingX={1}
                textAlign="left"
                width="100%"
                background="transparent"
                border="0"
              >
                <Text textStyle="headingItem">{org.name}</Text>
                <Text textStyle="textContext">
                  {org.topLevelOrganizationName}
                </Text>
                <Text textStyle="textContext">{org.formattedLocation}</Text>
              </ListItem>
            );
          })}
          <Box
            borderTopWidth="1px"
            borderStyle="solid"
            borderColor="neutralPrimaryBorder"
            paddingTop={4}
            paddingX={2}
          >
            <Banner
              variant="attention"
              heading="No matching results?"
              content={
                <Flex flexDirection={'column'} gap={2}>
                  <Text>
                    If your Affiliation is not already present, please refine
                    your search criteria or add the Affiliation manually.
                  </Text>
                  <Box marginStart={-1}>
                    <ButtonText
                      data-cy="ringgold-add-unverified-affiliation-button"
                      onClick={() => {
                        handleAddUnverifiedAffiliations();
                      }}
                    >
                      + Add Affiliation Manually
                    </ButtonText>
                  </Box>
                </Flex>
              }
            />
          </Box>
        </List>
      );
    };

    const onClosePopUp = () => {
      if (suppressNextClose.current) {
        suppressNextClose.current = false;
        return;
      }
      inputRef.current?.focus();
      onClose();

      setTimeout(() => {
        if (document.activeElement === inputRef.current) {
          (document.activeElement as HTMLElement)?.blur();
        }
      }, 0);
    };

    useEffect(() => {
      const handleOutsideClick = (e: MouseEvent) => {
        if (
          isOpen &&
          wrapperRef.current &&
          !wrapperRef.current.contains(e.target as Node)
        ) {
          onClose();
        }
      };
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, onClose]);

    return (
      <>
        {isAddAffiliationManually ? (
          <Flex gap={4} flexDirection="column">
            <Flex>
              <ButtonText
                aria-label="Back to affiliation search"
                onClick={() => setIsAddAffiliationManually(false)}
                leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back to Search
              </ButtonText>
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <FieldLabel label="Organization Name" isRequired subText="" />
              <Input
                {...register(`metadata.authors.${index}.affiliation`, {
                  required: 'Affiliation is required'
                })}
                placeholder="e.g., Molecular Connections"
                isInvalid={!!errors?.metadata?.authors?.[index]?.affiliation}
              />
              {errors?.metadata?.authors?.[index]?.affiliation &&
                renderSearchFieldErrorMessage(
                  errors?.metadata?.authors?.[index]?.affiliation.message
                )}
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <FieldLabel label="City" isRequired subText="" />
              <Input
                {...register(`metadata.authors.${index}.city`, {
                  required: 'City is required'
                })}
                placeholder="e.g., London"
                isInvalid={!!errors?.metadata?.authors?.[index]?.city}
              />
              {errors?.metadata?.authors?.[index]?.city && (
                <FormFeedback variant="error">
                  {errors?.metadata?.authors?.[index]?.city?.message}
                </FormFeedback>
              )}
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <FieldLabel label="Country" isRequired subText="" />
              <CountrySearchSelect
                value={selectedCountry}
                onChange={(country: AffiliationCountryList) => {
                  setSelectedCountry(country);
                  clearErrors(`metadata.authors.${index}.country`);
                }}
                isInvalid={!!errors?.metadata?.authors?.[index]?.country}
              />
              {errors?.metadata?.authors?.[index]?.country && (
                <FormFeedback variant="error">
                  {errors?.metadata?.authors?.[index]?.country?.message}
                </FormFeedback>
              )}
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <FieldLabel label="Postal Code" isRequired={false} subText="" />
              <Input
                {...register(`metadata.authors.${index}.postalCode`)}
                placeholder="e.g., 10001"
                isInvalid={!!errors?.metadata?.authors?.[index]?.postalCode}
              />
              {errors?.metadata?.authors?.[index]?.postalCode && (
                <FormFeedback variant="error">
                  {errors?.metadata?.authors?.[index]?.postalCode?.message}
                </FormFeedback>
              )}
            </Flex>
          </Flex>
        ) : (
          <>
            <Box ref={wrapperRef}>
            <Popover
              returnFocusOnClose={true}
              isOpen={isOpen}
              onClose={onClosePopUp}
              placement="bottom"
              closeOnBlur={false}
              matchWidth
              autoFocus={false}
            >
              <PopoverTrigger>
                <Input
                  ref={inputRef}
                  aria-label="Search affiliation"
                  aria-haspopup="listbox"
                  aria-controls={isOpen ? 'ringgold-search-results' : undefined}
                  aria-autocomplete="list"
                  {...props}
                  onChange={handleChange}
                  onFocus={handleChange}
                  onMouseDown={() => {
                    if (isOpen) suppressNextClose.current = true;
                  }}
                  data-cy="ringgold-search-input"
                  autoComplete="off"
                />
              </PopoverTrigger>
              <PopoverContent
                id="ringgold-search-results"
                role="listbox"
                minW={{ base: '100%' }}
                paddingY={4}
                paddingX={2}
                maxHeight={
                  isLoading || !ringgoldOrgs
                    ? 'none'
                    : { base: '40vh', md: '264px' }
                }
                overflowY={isLoading || !ringgoldOrgs ? 'unset' : 'auto'}
              >
                <PopoverBody padding={0}>{renderAffiliationList()}</PopoverBody>
              </PopoverContent>
            </Popover>
            </Box>
            {isVerified && (
              <Text textStyle="textBody" mt={2}>
                This affiliation is verified by Ringgold, ensuring institutional
                authenticity. Editors will see this verification status during
                their review.
              </Text>
            )}
          </>
        )}
      </>
    );
  }
);

export default AuthorRinggoldSearch;
